import _ from 'lodash';
import { type FC, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/app/hooks/auth-provider';
import { Description, Field, FieldGroup, Fieldset, Label } from '@/primitives/fieldset.tsx';
import { Select } from '@/primitives/select.tsx';
import { Switch } from '@/primitives/switch.tsx';
import { useFetchAccountCategoriesQuery } from '@/slices/account-api-slice.ts';
import {
  useFetchRepresentativeStatusesQuery,
  useFetchRepSubscriptionStatusesQuery,
} from '@/slices/representative-api-slice.ts';
import { useSecurityContext } from '../../hooks/security-provider.tsx';
import type { AccountListModel } from '../../types/account.type.ts';
import type { AccountCategoryModel } from '../../types/account-category.type.ts';
import type { RepresentativeSearchParams } from '../../types/representative.type.ts';
import type { RepresentativeStatusModel } from '../../types/representative-status.type.ts';
import {
  keywordDefinition,
  type SearchCriteriaDisplayDefinition,
} from '../../types/search-form.type.ts';
import { UserTypeEnum } from '../../types/user-type.ts';
import AccountCategorySelector from '../account/account-category-selector.tsx';
import SearchForm from '../search-form.tsx';
import RepresentativeStatusSelector from './representative-status-selector.tsx';

interface RepresentativeSearchFormModel {
  keyword: string;
  account?: AccountListModel;
  representativeStatus?: RepresentativeStatusModel;
  accountCategory?: AccountCategoryModel;
  activeOnly?: boolean;
  subscriptionStatus?: string;
}

const RepresentativeSearchFilterForm = () => {
  const { user } = useAuth();
  const { currentAccount } = useSecurityContext();
  const { control, register } = useFormContext<RepresentativeSearchParams>();
  const { data: subscriptionStatuses } = useFetchRepSubscriptionStatusesQuery();
  return (
    <Fieldset>
      <FieldGroup>
        {user.userType === UserTypeEnum.StaffMember && (
          <>
            <div className='rounded-xl border border-default-200 bg-default-50 px-4 py-3'>
              <div className='flex items-start gap-3'>
                <Controller
                  control={control}
                  name='activeOnly'
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Switch
                      id='rep-filter-active-only'
                      color='blue'
                      className='mt-0.5 shrink-0'
                      onChange={onChange}
                      checked={value}
                      onBlur={onBlur}
                    />
                  )}
                />
                <div className='min-w-0'>
                  <Label
                    htmlFor='rep-filter-active-only'
                    className='cursor-pointer font-semibold text-default-800'
                  >
                    Active representatives only
                  </Label>
                  <Description className='mt-0.5 text-default-500'>
                    Hide representatives who are not active on this account
                  </Description>
                </div>
              </div>
            </div>
            <RepresentativeStatusSelector name='representativeStatus' />
            <AccountCategorySelector name='accountCategory' accountId={currentAccount?.id} />
          </>
        )}

        {user.userType === UserTypeEnum.Admin && (
          <Field>
            <Label>Subscription Status</Label>
            <Select {...register('subscriptionStatus')}>
              <option value=''>--Subscription Status--</option>
              {subscriptionStatuses?.length &&
                subscriptionStatuses.map(value => (
                  <option key={value} value={value}>
                    {_.capitalize(value)}
                  </option>
                ))}
            </Select>
          </Field>
        )}
      </FieldGroup>
    </Fieldset>
  );
};

const RepresentativeSearchForm: FC = () => {
  const { currentAccount } = useSecurityContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const getSearchModel = (value: URLSearchParams): RepresentativeSearchFormModel => {
    // biome-ignore lint/correctness/useHookAtTopLevel: fix me
    const { data: statuses } = useFetchRepresentativeStatusesQuery();
    // biome-ignore lint/correctness/useHookAtTopLevel: fix me
    const { data: categories } = useFetchAccountCategoriesQuery();

    const status = statuses?.find(x => x.id === parseInt(value.get('representativeStatus')));
    const category = categories?.find(x => x.id === parseInt(value.get('accountCategory')));

    return {
      keyword: value.get('keyword'),
      account: currentAccount,
      activeOnly: value.get('activeOnly') === 'true',
      representativeStatus: value.has('representativeStatus') ? status : null,
      accountCategory: value.has('accountCategory') ? category : null,
      subscriptionStatus: value.get('subscriptionStatus'),
    };
  };

  useEffect(() => {
    if (searchParams.get('accountKey') !== currentAccount?.key) {
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);

        if (currentAccount?.key) {
          params.set('accountKey', currentAccount.key);
        } else {
          params.delete('accountKey');
        }

        return params;
      });
    }
  }, [currentAccount]);

  const definitions: SearchCriteriaDisplayDefinition[] = [
    keywordDefinition,
    {
      key: 'account',
      label: 'Account',
      displayFunc: (value: AccountListModel) => value?.accountName,
    },
    {
      key: 'representativeStatus',
      label: 'Status',
      displayFunc: (value: RepresentativeStatusModel) => value?.description,
    },
    {
      key: 'accountCategory',
      label: 'Category',
      displayFunc: (value: AccountCategoryModel) => value?.description,
    },
    {
      key: 'activeOnly',
      label: 'Active representatives only',
      displayFunc: (value: boolean) => (value ? 'Yes' : null),
    },
    {
      key: 'subscriptionStatus',
      label: 'Subscription Status',
      displayFunc: (value: string) => _.capitalize(value),
    },
  ];

  const getUrlSearchParams = (value: RepresentativeSearchFormModel) => {
    const params = new URLSearchParams();
    if (
      value?.activeOnly ||
      value?.subscriptionStatus ||
      value?.account?.id ||
      value?.representativeStatus?.id >= 0 ||
      value?.accountCategory?.id ||
      value?.keyword
    ) {
      if (value.activeOnly) {
        params.set('activeOnly', 'true');
      }

      if (value.subscriptionStatus) {
        params.set('subscriptionStatus', value.subscriptionStatus);
      }

      if (value.keyword) {
        params.set('keyword', value.keyword.trim());
      }

      if (
        value.representativeStatus &&
        _.isNumber(parseInt(value.representativeStatus.id.toString()))
      ) {
        params.set('representativeStatus', value.representativeStatus.id.toString());
      }

      if (value.accountCategory && _.isNumber(parseInt(value.accountCategory.id.toString()))) {
        params.set('accountCategory', value.accountCategory.id.toString());
      }

      if (value.account?.key) {
        params.set('accountKey', value.account.key);
      }

      return params;
    }
  };

  const defaultValue = {
    account: { id: '' },
    accountCategory: { id: '' },
    representativeStatus: { id: '' },
    keyword: '',
    activeOnly: false,
    subscriptionStatus: '',
  };

  return (
    <SearchForm<RepresentativeSearchFormModel>
      defaultValue={defaultValue}
      searchCriteriaDisplayDefinitions={definitions}
      getSearchParamModel={getSearchModel}
      getUrlSearchParams={getUrlSearchParams}
      filterForm={<RepresentativeSearchFilterForm />}
    />
  );
};

export default RepresentativeSearchForm;
